import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { useTranslation } from "../../i18n";

/**
 * The single "Create" control of the marketing page, opening a small menu.
 *
 * Creating a campaign and creating a segment are different jobs that happen at
 * the same moment — an operator setting up a broadcast usually needs an
 * audience segment for it — so both hang off one button instead of two
 * competing ones. The menu closes on outside click and on Escape so it never
 * traps the page.
 */
export default function CreateMenu({ items, disabled = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <Button
        size="sm"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {t("campaign.create")}
        <ChevronDown size={14} aria-hidden="true" className="ml-1" />
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 w-64 rounded-lg border border-c-gray-400/30 bg-c-gray-600 p-1 shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className="w-full text-left px-3 py-2 rounded hover:bg-c-gray-500 cursor-pointer"
            >
              <span className="block text-sm">{item.label}</span>
              {item.description && (
                <span className="block text-xs text-c-gray-400">
                  {item.description}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
